import {
  mercatorX,
  mercatorY,
} from '~/client/src/shared/components/MapBoxViewer/rasterTileMath'

import { MAX_ZOOM_LEVEL } from './mapZoomRule'
import { COLLISION_PADDING, MARKER_COLLISION_HALF_PX } from './markerGeometry'

// A leaf module on purpose, map geometry its only import: the location layers that stack are
// federated into JHP, and the stack list lives in the Workflows map.

type PointFeature = GeoJSON.Feature<GeoJSON.Point>

export interface IStackMembers {
  locationIds: string[]
  longitude: number
  latitude: number
  // Every member's own point, once each: near-co-located members keep their own.
  points: Array<{ longitude: number; latitude: number }>
}

// Mapbox stringifies array properties, so the members travel as JSON.
export const parseStackMembers = (
  properties: GeoJSON.GeoJsonProperties,
): IStackMembers | null =>
  typeof properties?.stackMembers === 'string'
    ? (JSON.parse(properties.stackMembers) as IStackMembers)
    : null

// A label-only or data-less item is an annotation, not an object a stack could list or select; a
// lifted location is the selected one, and an outlined one carries the editor's ring.
const isStackableLocation = ({ properties }: PointFeature): boolean =>
  properties?.showIcon === true &&
  properties.isLifted !== true &&
  properties.isOutlined !== true &&
  properties.isDataLess !== true

// Markers this close can never be told apart: their collision boxes still overlap at the deepest
// zoom the map shows, so at every zoom collision hides all but one. Mapbox collides square boxes,
// the raster's with its padding, so the reach is a box too, per axis in web-mercator px.
const STACK_ZOOM = MAX_ZOOM_LEVEL
const WORLD_PX = 512 * 2 ** STACK_ZOOM
const STACK_REACH_PX = 2 * (MARKER_COLLISION_HALF_PX + COLLISION_PADDING)

const worldPoint = ([longitude, latitude]: number[]): {
  x: number
  y: number
} => ({ x: mercatorX(longitude) * WORLD_PX, y: mercatorY(latitude) * WORLD_PX })

interface IGroup {
  point: { x: number; y: number }
  features: PointFeature[]
}

// Each joins the nearest lead within reach, never a member: a row of markers a hand apart does
// not chain into one stack.
function groupInseparable(features: PointFeature[]): IGroup[] {
  const groups: IGroup[] = []
  const cells = new Map<string, IGroup[]>()
  const cellOf = (value: number) => Math.floor(value / STACK_REACH_PX)
  const sorted = [...features].sort(
    (a, b) =>
      (a.properties?.sortKey as number) - (b.properties?.sortKey as number),
  )
  for (const feature of sorted) {
    const point = worldPoint(feature.geometry.coordinates)
    const cx = cellOf(point.x)
    const cy = cellOf(point.y)
    let nearest: IGroup | undefined
    let nearestDistance = STACK_REACH_PX
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (const group of cells.get(`${cx + dx},${cy + dy}`) ?? []) {
          const distance = Math.max(
            Math.abs(group.point.x - point.x),
            Math.abs(group.point.y - point.y),
          )
          if (distance < nearestDistance) {
            nearest = group
            nearestDistance = distance
          }
        }
      }
    }
    if (nearest) {
      nearest.features.push(feature)
      continue
    }
    const group = { point, features: [feature] }
    groups.push(group)
    const key = `${cx},${cy}`
    cells.set(key, [...(cells.get(key) ?? []), group])
  }
  return groups
}

// Locations that cannot be told apart draw as one marker, the one collision would keep (lowest
// sortKey), which carries the count and the members. The others leave the source. The lead's
// exact coordinates travel with the members — a rendered feature's geometry comes back quantised
// — and so does every member's own point, where its Workflows are pinned.
// `canStack` lets a host keep some groups apart (an export fans equal-status objects instead).
export function stackLocations(
  collection: GeoJSON.FeatureCollection<GeoJSON.Point>,
  canStack?: (locationIds: string[]) => boolean,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const rest: PointFeature[] = []
  const stackable: PointFeature[] = []
  for (const feature of collection.features) {
    if (isStackableLocation(feature)) {
      stackable.push(feature)
    } else {
      rest.push(feature)
    }
  }

  const leads = groupInseparable(stackable).flatMap(({ features }) => {
    if (features.length === 1) return features
    const locationIds = features.map(f => f.properties?.itemId as string)
    if (canStack && !canStack(locationIds)) return features
    const [lead] = features
    const [longitude, latitude] = lead.geometry.coordinates
    const points = [
      ...new Map(
        features.map(
          ({
            geometry: {
              coordinates: [lng, lat],
            },
          }) => [`${lng},${lat}`, { longitude: lng, latitude: lat }],
        ),
      ).values(),
    ]
    return [
      {
        ...lead,
        properties: {
          ...lead.properties,
          stackCount: features.length,
          stackMembers: JSON.stringify({
            locationIds,
            longitude,
            latitude,
            points,
          } satisfies IStackMembers),
        },
      },
    ]
  })

  return { ...collection, features: [...rest, ...leads] }
}
